import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Copy, Gift, Link as LinkIcon, Users } from 'lucide-react'
import { AccountLayout } from './-layout'
import { accountQueries } from '@/services/accountApi'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { CurrencyDisplay } from '@/components/ui/currency-display'
import { toast } from 'sonner'
import type { ReferralReward } from '@/types/account'

export const Route = createFileRoute('/$lang/account/referrals')({
  component: ReferralsPage,
})

function ReferralsPage() {
  const { data, isLoading } = useQuery(accountQueries.referrals())

  const referral = data
  const affiliateApplication = referral?.affiliateApplication
  const referralLink = buildReferralLink(referral?.referralCode)
  const currentProgrammeReward = rewardForCurrency(referral?.currentProgramme?.rewards, referral?.accountCurrency)
  const affiliateFlatFeeReward = rewardForCurrency(affiliateApplication?.rewards, affiliateApplication?.accountCurrency)

  const handleCopy = async () => {
    if (!referralLink) return
    await navigator.clipboard.writeText(referralLink)
    toast.success('Referral link copied')
  }

  return (
    <AccountLayout>
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard title="People referred" value={referral?.totalReferredCount ?? 0} icon={Users} />
          <StatCard
            title="Total earned"
            value={<CurrencyDisplay amount={referral?.totalEarnedCents ?? 0} currency={affiliateApplication?.accountCurrency ?? referral?.accountCurrency ?? 'GBP'} inCents hideSign />}
            icon={Gift}
          />
          <StatCard
            title="Pending"
            value={<CurrencyDisplay amount={referral?.pendingEarnedCents ?? 0} currency={affiliateApplication?.accountCurrency ?? referral?.accountCurrency ?? 'GBP'} inCents hideSign />}
            icon={LinkIcon}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Your referral link</CardTitle>
            <CardDescription>Share this link with other users to earn credits when they make their first top-up.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input readOnly value={referralLink} placeholder={isLoading ? 'Loading...' : 'No referral link available'} />
              <Button onClick={handleCopy} disabled={!referralLink}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
            {referral?.currentProgramme && currentProgrammeReward && (
              <p className="text-sm text-muted-foreground">
                Current reward: you get <CurrencyDisplay amount={currentProgrammeReward.referrerCreditCents ?? 0} currency={currentProgrammeReward.currency} inCents hideSign /> and they get <CurrencyDisplay amount={currentProgrammeReward.refereeCreditCents ?? 0} currency={currentProgrammeReward.currency} inCents hideSign /> after their first top-up.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Affiliate programme</CardTitle>
            <CardDescription>Affiliates are enabled by the admin team and can have custom reward terms.</CardDescription>
          </CardHeader>
          <CardContent>
            {!affiliateApplication && (
              <p className="text-sm text-muted-foreground">
                Contact support if you want an affiliate code enabled on your account.
              </p>
            )}

            {affiliateApplication && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Badge>
                    {affiliateApplication.status ?? 'active'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">Code: {affiliateApplication.code}</span>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <StatCard title="Flat fee" value={<CurrencyDisplay amount={affiliateFlatFeeReward?.flatFeeCents ?? 0} currency={affiliateFlatFeeReward?.currency ?? affiliateApplication.accountCurrency} inCents hideSign />} />
                  <StatCard title="Revenue share" value={`${(Number(affiliateApplication.sharePercentage) * 100).toFixed(1)}%`} />
                  <StatCard title="Duration" value={`${affiliateApplication.revenueShareMonths} months`} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AccountLayout>
  )
}

function rewardForCurrency(rewards?: ReferralReward[] | null, currency?: string | null) {
  if (!rewards?.length) return null
  if (!currency) return rewards[0]
  return rewards.find((reward) => reward.currency === currency) ?? null
}

function buildReferralLink(referralCode?: string | null) {
  if (!referralCode || typeof window === 'undefined') return ''
  return `${window.location.origin}/?ref=${encodeURIComponent(referralCode)}`
}

function StatCard({ title, value, icon: Icon }: { title: string; value: React.ReactNode; icon?: React.ElementType }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-6">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-semibold">{value}</p>
        </div>
        {Icon ? <Icon className="h-5 w-5 text-muted-foreground" /> : null}
      </CardContent>
    </Card>
  )
}
